/**
 * Rides Context
 * 
 * Manages rides in Atlas, providing a bridge between RideSchema state
 * and Atlas's map layers (Lines). Similar to FixedRoutesContext but
 * specifically for rides (booking requests).
 * 
 * Rides are stored internally as RideSchema[] and synced with map layers as Lines
 * to maintain compatibility with existing simulation infrastructure.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { RideSchema } from '../types/simulation';
import { AppContext } from './context';
import type { Line, Layer } from '../context/context';
import { convertRideSchemaToLine } from '../services/fixedRouteUatImporter';
import { v4 as uuid4 } from 'uuid';
import { applyRideAddressPlaceholders, applyRideAddressPlaceholdersToList, lineAddressPlaceholders } from '../utils/rides/addressPlaceholders';

/**
 * Rides state interface
 */
export interface RidesState {
  rides: RideSchema[];
}

/**
 * Rides context type
 */
interface RidesContextType extends RidesState {
  addRide: (ride: RideSchema) => void;
  updateRide: (ride: RideSchema) => void;
  removeRide: (rideId: string) => void;
  getRideById: (rideId: string) => RideSchema | undefined;
  getAllRides: () => RideSchema[];
  bulkAddRides: (rides: RideSchema[], mergeMode: 'replace' | 'merge') => void;
  clearAllRides: () => void;
  selectedRideIds: Set<string>;
  setSelectedRideIds: (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  selectRide: (rideId: string) => void;
  deselectRide: (rideId: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  // Filtering and search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredRides: RideSchema[];
}

const RidesContext = createContext<RidesContextType | undefined>(undefined);

/**
 * RidesContextProvider Component
 * 
 * Provides rides state management and syncs with map layers.
 */
export function RidesContextProvider({ children }: { children: React.ReactNode }) {
  const [rides, setRides] = useState<RideSchema[]>([]);
  const [selectedRideIds, setSelectedRideIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Note: RidesContext manages rides as RideSchema[] internally.
  // Map layer sync is handled by import/export functions (importUATRides, etc.)
  // This avoids conflicts and allows rides to exist in context without being on the map.

  // Filter rides based on search query
  const filteredRides = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return rides;
    }
    
    const query = searchQuery.toLowerCase();
    return rides.filter(ride => {
      const fromAddress = ride.from_address?.toLowerCase() || '';
      const toAddress = ride.to_address?.toLowerCase() || '';
      const rideId = ride.ride_id?.toLowerCase() || '';
      const userId = ride.user_id?.toLowerCase() || '';
      
      return fromAddress.includes(query) ||
             toAddress.includes(query) ||
             rideId.includes(query) ||
             userId.includes(query);
    });
  }, [rides, searchQuery]);

  const addRide = useCallback((ride: RideSchema) => {
    // Validate ride has required coordinates
    if (!isValidRide(ride)) {
      console.error('[RidesContext] Cannot add ride - invalid coordinates:', ride);
      return;
    }
    
    setRides((prev) => {
      const normalizedRide = applyRideAddressPlaceholders(ride, prev.length + 1);
      // Check for duplicates
      const exists = prev.find(r => 
        r.ride_id === normalizedRide.ride_id || 
        r.external_reference_id === normalizedRide.external_reference_id
      );
      if (exists) {
        console.warn('[RidesContext] Ride already exists, updating instead:', normalizedRide.ride_id || normalizedRide.external_reference_id);
        return prev.map(r => 
          (r.ride_id === normalizedRide.ride_id || r.external_reference_id === normalizedRide.external_reference_id) 
            ? normalizedRide
            : r
        );
      }
      return [...prev, normalizedRide];
    });
  }, []);

  const updateRide = useCallback((updatedRide: RideSchema) => {
    if (!isValidRide(updatedRide)) {
      console.error('[RidesContext] Cannot update ride - invalid coordinates:', updatedRide);
      return;
    }
    
    setRides((prev) => {
      const normalizedRide = applyRideAddressPlaceholders(updatedRide, 1);
      return (
      prev.map((r) => 
        (r.ride_id === normalizedRide.ride_id || 
         r.external_reference_id === normalizedRide.external_reference_id) 
          ? normalizedRide
          : r
      )
    );
    });
  }, []);

  const removeRide = useCallback((rideId: string) => {
    setRides((prev) => 
      prev.filter((r) => 
        r.ride_id !== rideId && r.external_reference_id !== rideId
      )
    );
    // Also remove from selection
    setSelectedRideIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rideId);
      return newSet;
    });
  }, []);

  const getRideById = useCallback((rideId: string): RideSchema | undefined => {
    return rides.find(r => 
      r.ride_id === rideId || r.external_reference_id === rideId
    );
  }, [rides]);

  const getAllRides = useCallback((): RideSchema[] => {
    return rides;
  }, [rides]);

  const bulkAddRides = useCallback((newRides: RideSchema[], mergeMode: 'replace' | 'merge') => {
    // Filter out rides with invalid coordinates
    const validRides = newRides.filter(ride => {
      if (!isValidRide(ride)) {
        console.warn(`[RidesContext] Filtering out ride with invalid coordinates:`, ride);
        return false;
      }
      return true;
    });
    
    if (validRides.length !== newRides.length) {
      console.warn(`[RidesContext] Filtered out ${newRides.length - validRides.length} rides with invalid coordinates`);
    }
    
    const normalizedRides = applyRideAddressPlaceholdersToList(validRides);

    if (mergeMode === 'replace') {
      setRides(normalizedRides);
      return;
    }
    
    // Merge mode: handle duplicates
    setRides((prev) => {
      const rideMap = new Map<string, RideSchema>();
      
      // Add existing rides
      prev.forEach(ride => {
        const key = ride.ride_id || ride.external_reference_id || uuid4();
        rideMap.set(key, ride);
      });
      
      // Add/update with new rides
      normalizedRides.forEach(ride => {
        const key = ride.ride_id || ride.external_reference_id || uuid4();
        rideMap.set(key, ride);
      });
      
      return Array.from(rideMap.values());
    });
  }, []);

  const clearAllRides = useCallback(() => {
    setRides([]);
    setSelectedRideIds(new Set());
  }, []);

  const selectRide = useCallback((rideId: string) => {
    setSelectedRideIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(rideId);
      return newSet;
    });
  }, []);

  const deselectRide = useCallback((rideId: string) => {
    setSelectedRideIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rideId);
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedRideIds(new Set(rides.map(r => r.ride_id || r.external_reference_id || '').filter(Boolean)));
  }, [rides]);

  const deselectAll = useCallback(() => {
    setSelectedRideIds(new Set());
  }, []);

  const value: RidesContextType = {
    rides,
    addRide,
    updateRide,
    removeRide,
    getRideById,
    getAllRides,
    bulkAddRides,
    clearAllRides,
    selectedRideIds,
    setSelectedRideIds,
    selectRide,
    deselectRide,
    selectAll,
    deselectAll,
    searchQuery,
    setSearchQuery,
    filteredRides,
  };

  return <RidesContext.Provider value={value}>{children}</RidesContext.Provider>;
}

/**
 * useRides Hook
 * 
 * Provides access to RidesContext.
 * 
 * @throws Error if used outside RidesContextProvider
 */
export function useRides(): RidesContextType {
  const context = useContext(RidesContext);
  if (context === undefined) {
    throw new Error('useRides must be used within RidesContextProvider');
  }
  return context;
}

/**
 * Helper function to validate ride coordinates
 */
function isValidRide(ride: RideSchema): boolean {
  const fromLat = ride.from_latitude;
  const fromLng = ride.from_longitude;
  const toLat = ride.to_latitude;
  const toLng = ride.to_longitude;
  
  return (
    fromLat !== undefined &&
    fromLng !== undefined &&
    toLat !== undefined &&
    toLng !== undefined &&
    fromLat !== null &&
    fromLng !== null &&
    toLat !== null &&
    toLng !== null &&
    typeof fromLat === 'number' &&
    typeof fromLng === 'number' &&
    typeof toLat === 'number' &&
    typeof toLng === 'number' &&
    !isNaN(fromLat) &&
    !isNaN(fromLng) &&
    !isNaN(toLat) &&
    !isNaN(toLng) &&
    isFinite(fromLat) &&
    isFinite(fromLng) &&
    isFinite(toLat) &&
    isFinite(toLng) &&
    fromLat >= -90 &&
    fromLat <= 90 &&
    fromLng >= -180 &&
    fromLng <= 180 &&
    toLat >= -90 &&
    toLat <= 90 &&
    toLng >= -180 &&
    toLng <= 180
  );
}

/**
 * Convert Line back to RideSchema
 * 
 * Utility function to convert a Line (from map layers) back to RideSchema.
 * Used when loading rides from map layers or converting map data to ride data.
 * 
 * @param line - Line object from map layers
 * @returns RideSchema or null if conversion fails
 */
export function lineToRideSchema(line: Line): RideSchema | null {
  if (line.type !== 'line' || !Array.isArray(line.coordinates) || line.coordinates.length < 2) {
    return null;
  }
  
  const from = line.coordinates[0];
  const to = line.coordinates[1];
  
  if (!from || !to || from.length < 2 || to.length < 2) {
    return null;
  }
  
  // Extract passenger counts from line
  const passengers: Array<{ type: string; number_of_passengers: number }> = [];
  
  // Add standard passengers (only if count > 0)
  const standardCount = Number(line.standard_seat ?? line.numPassengers ?? 0);
  if (standardCount > 0) {
    passengers.push({
      type: 'standard',
      number_of_passengers: standardCount,
    });
  }
  
  // Add wheelchair passengers (only if count > 0)
  const wheelchairCount = Number(line.wheelchair ?? 0);
  if (wheelchairCount > 0) {
    passengers.push({
      type: 'wheelchair',
      number_of_passengers: wheelchairCount,
    });
  }
  
  // Default to 1 standard passenger if no passengers specified
  if (passengers.length === 0) {
    passengers.push({ type: 'standard', number_of_passengers: 1 });
  }
  
  const ordinal = inferAddressOrdinalFromLine(line);
  const { from_address, to_address } = lineAddressPlaceholders(line as unknown as Record<string, unknown>, ordinal);
  const ride: RideSchema = {
    external_reference_id: line.ride_id,
    external_user_reference_id: line.user_id,
    from_latitude: from[0],
    from_longitude: from[1],
    to_latitude: to[0],
    to_longitude: to[1],
    from_address,
    to_address,
    request_type: line.request_type || (line as any).requestType || undefined,
    passengers,
    request_time: line.time ? String(line.time) : (line as any).request_time || undefined,
    created_at: (line as any).uatMetadata?.created_at || undefined,
    batch_id: (line as any).uatMetadata?.batch_id || undefined,
    priority: (line as any).uatMetadata?.priority || undefined,
    ride_id: line.ride_id,
    user_id: line.user_id,
    from_point_id: (line as any).originPointId || (line as any).uatMetadata?.from_point_id || undefined,
    to_point_id: (line as any).destPointId || (line as any).uatMetadata?.to_point_id || undefined,
  };
  
  return ride;
}

function inferAddressOrdinalFromLine(line: Line): number {
  const candidate = String(line.ride_id || line.name || line.id || '');
  const match = candidate.match(/(\d+)/);
  const parsed = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * Load rides from map layers
 * 
 * Utility function to extract RideSchema[] from existing map layers.
 * Used to initialize RidesContext with rides that already exist on the map.
 * 
 * @param mapLayers - Array of map layers to search for ride lines
 * @returns Array of RideSchema objects extracted from map layers
 */
export function loadRidesFromMapLayers(mapLayers: Layer[]): RideSchema[] {
  const loadedRides: RideSchema[] = [];
  
  for (const layer of mapLayers) {
    for (const shape of layer.shapes) {
      if (shape.type === 'line') {
        const line = shape as Line;
        // Check if this Line looks like a ride:
        // - Has coordinates (origin and destination)
        // - Has time or request_time
        // - Has ride_id OR has user_id/numPassengers (indicating it's a ride, not a route)
        const hasCoordinates = Array.isArray(line.coordinates) && line.coordinates.length >= 2;
        const hasTime = !!(line.time || (line as any).request_time);
        const hasRideId = !!(line.ride_id || (line as any).rideId);
        // Check for ride indicators - use type assertion to access properties that may exist
        const hasRideIndicators = !!(
          (line as any).user_id || 
          (line as any).userId || 
          line.numPassengers || 
          (line as any).numPassengers ||
          (line as any).wheelchair
        );
        
        // Consider it a ride if it has coordinates and either:
        // 1. Has ride_id, OR
        // 2. Has time/request_time AND has ride indicators (user_id, numPassengers, wheelchair)
        if (hasCoordinates && (hasRideId || (hasTime && hasRideIndicators))) {
          const ride = lineToRideSchema(line);
          if (ride) {
            loadedRides.push(ride);
          }
        }
      }
    }
  }
  
  return loadedRides;
}

